import clsx from "clsx";
import {
  ArrowLeftRight,
  CopyCheck,
  Dumbbell,
  FileUp,
  LayoutDashboard,
  LogOut,
  Menu,
  PieChart,
  Plus,
  Settings,
  Tags,
  Target,
  Wallet,
  type LucideIcon,
} from "lucide-react";
import { createContext, useCallback, useContext, useMemo, useState } from "react";
import { NavLink, Outlet, useLocation } from "react-router-dom";

import { ThemeToggle } from "./ui/ThemeToggle";
import { useAuth } from "@/auth/AuthContext";
import { TransactionForm, type TransactionFormProps } from "@/features/transactions/TransactionForm";
import { SyncBanner, SyncedAgo } from "./SyncBanner";
import { Modal } from "./ui/Modal";

interface NavItem {
  to: string;
  label: string;
  icon: LucideIcon;
}

const PRIMARY: NavItem[] = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard },
  { to: "/transactions", label: "Transactions", icon: ArrowLeftRight },
  { to: "/accounts", label: "Accounts", icon: Wallet },
  { to: "/budgets", label: "Budgets", icon: Target },
  { to: "/reports", label: "Reports", icon: PieChart },
];
const SECONDARY: NavItem[] = [
  { to: "/fitness", label: "Fitness", icon: Dumbbell },
  { to: "/imports", label: "Import", icon: FileUp },
  { to: "/review", label: "Duplicates", icon: CopyCheck },
  { to: "/categories", label: "Categories", icon: Tags },
  { to: "/settings", label: "Settings", icon: Settings },
];

type QuickAddOptions = Omit<TransactionFormProps, "onDone">;
const QuickAddContext = createContext<(opts?: QuickAddOptions) => void>(() => undefined);
export const useQuickAdd = () => useContext(QuickAddContext);

export function AppShell() {
  const { user, logout } = useAuth();
  const [quickAdd, setQuickAdd] = useState<QuickAddOptions | null>(null);
  const [moreOpen, setMoreOpen] = useState(false);
  const location = useLocation();
  const open = useCallback((opts?: QuickAddOptions) => setQuickAdd(opts ?? {}), []);
  const ctx = useMemo(() => open, [open]);
  const editing = Boolean(quickAdd?.transaction);

  return (
    <QuickAddContext.Provider value={ctx}>
      <a href="#main" className="sr-only focus:not-sr-only focus:fixed focus:left-3 focus:top-3 focus:z-50 focus:rounded-lg focus:bg-surface focus:px-3 focus:py-2">
        Skip to content
      </a>
      <div className="min-h-dvh md:grid md:grid-cols-[232px_1fr]">
        {/* Desktop sidebar */}
        <aside className="sticky top-0 hidden h-dvh flex-col border-r border-border bg-surface px-3 py-4 md:flex">
          <div className="mb-4 flex items-center gap-2 px-2">
            <img src="/favicon.svg" alt="" className="size-7" />
            <span className="text-base font-semibold">Ledger</span>
          </div>
          <button
            type="button"
            onClick={() => open()}
            className="mb-4 inline-flex h-10 items-center justify-center gap-2 rounded-lg bg-accent px-3 text-sm font-medium text-on-accent hover:bg-accent-hover"
          >
            <Plus className="size-4" aria-hidden /> Add transaction
          </button>
          <nav aria-label="Main" className="flex flex-1 flex-col gap-0.5">
            {PRIMARY.map((item) => <SideLink key={item.to} item={item} />)}
            <div className="my-3 border-t border-line" />
            {SECONDARY.map((item) => <SideLink key={item.to} item={item} />)}
          </nav>
          <div className="mt-4 border-t border-line pt-3">
            <div className="mb-3 px-2">
              <ThemeToggle compact />
            </div>
            <p className="truncate px-2 text-sm font-medium text-ink">{user?.full_name || user?.email}</p>
            <p className="truncate px-2 text-xs text-ink-3">{user?.email}</p>
            <SyncedAgo />
            <button
              type="button"
              onClick={() => void logout()}
              className="mt-2 flex w-full items-center gap-2 rounded-lg px-2 py-2 text-sm text-ink-2 hover:bg-surface-2 hover:text-ink"
            >
              <LogOut className="size-4" aria-hidden /> Sign out
            </button>
          </div>
        </aside>

        <main id="main" className="min-w-0 px-4 pb-28 pt-5 sm:px-6 md:pb-10 lg:px-8">
          <div className="mx-auto max-w-6xl">
            <SyncBanner />
            <Outlet />
          </div>
        </main>
      </div>

      {/* Mobile bottom tab bar */}
      <nav
        aria-label="Main"
        className="safe-bottom fixed inset-x-0 bottom-0 z-40 border-t border-border bg-surface md:hidden"
      >
        <div className="grid h-16 grid-cols-5 items-center">
          <TabLink item={PRIMARY[0]!} />
          <TabLink item={PRIMARY[1]!} />
          <div className="flex justify-center">
            <button
              type="button"
              onClick={() => open()}
              aria-label="Add transaction"
              className="-mt-6 flex size-14 items-center justify-center rounded-full bg-accent text-on-accent shadow-lg hover:bg-accent-hover"
            >
              <Plus className="size-7" aria-hidden />
            </button>
          </div>
          <TabLink item={PRIMARY[3]!} />
          <button
            type="button"
            onClick={() => setMoreOpen(true)}
            aria-haspopup="dialog"
            className={clsx(
              "flex flex-col items-center gap-0.5 text-[11px]",
              [PRIMARY[2]!, PRIMARY[4]!, ...SECONDARY].some((i) => location.pathname.startsWith(i.to))
                ? "text-accent"
                : "text-ink-3",
            )}
          >
            <Menu className="size-5" aria-hidden />
            More
          </button>
        </div>
      </nav>

      <Modal open={moreOpen} onClose={() => setMoreOpen(false)} title="More" size="sm">
        <nav aria-label="More" className="grid grid-cols-3 gap-2" onClick={() => setMoreOpen(false)}>
          {[PRIMARY[2]!, PRIMARY[4]!, ...SECONDARY].map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              className={({ isActive }) =>
                clsx(
                  "flex flex-col items-center gap-1.5 rounded-xl border border-border px-2 py-4 text-sm",
                  isActive ? "bg-accent-wash text-ink" : "text-ink-2 hover:bg-surface-2",
                )
              }
            >
              <item.icon className="size-5" aria-hidden />
              {item.label}
            </NavLink>
          ))}
          <button
            type="button"
            onClick={() => void logout()}
            className="flex flex-col items-center gap-1.5 rounded-xl border border-border px-2 py-4 text-sm text-ink-2 hover:bg-surface-2"
          >
            <LogOut className="size-5" aria-hidden /> Sign out
          </button>
        </nav>

        {/* Outside the nav: picking a theme is not navigating, and the nav's
            onClick closes the sheet on every tap - which would shut it the
            moment you chose a theme, before you could see the result. */}
        <div className="mt-4 flex items-center justify-between gap-3 border-t border-line pt-4">
          <span className="text-sm text-ink-2">Theme</span>
          <ThemeToggle />
        </div>
      </Modal>

      <Modal
        open={quickAdd !== null}
        onClose={() => setQuickAdd(null)}
        title={editing ? "Edit transaction" : "Add transaction"}
      >
        {quickAdd && <TransactionForm {...quickAdd} onDone={() => setQuickAdd(null)} />}
      </Modal>
    </QuickAddContext.Provider>
  );
}

function SideLink({ item }: { item: NavItem }) {
  return (
    <NavLink
      to={item.to}
      end={item.to === "/"}
      className={({ isActive }) =>
        clsx(
          "flex items-center gap-2.5 rounded-lg px-2.5 py-2 text-sm",
          isActive ? "bg-accent-wash font-medium text-ink" : "text-ink-2 hover:bg-surface-2 hover:text-ink",
        )
      }
    >
      <item.icon className="size-4" aria-hidden />
      {item.label}
    </NavLink>
  );
}

function TabLink({ item }: { item: NavItem }) {
  return (
    <NavLink
      to={item.to}
      end={item.to === "/"}
      className={({ isActive }) =>
        clsx("flex flex-col items-center gap-0.5 text-[11px]", isActive ? "text-accent" : "text-ink-3")
      }
    >
      <item.icon className="size-5" aria-hidden />
      {item.label}
    </NavLink>
  );
}
